import { useState } from 'react';
import { reviewContent, faqItems } from '@/mocks/seoContent';

export default function ReviewSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 bg-background-50" id="review">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            {reviewContent.title}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            An honest breakdown of the Sugargoo shopping experience to help you decide if it fits your needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-10">
          <div className="p-5 md:p-6 rounded-2xl bg-accent-50 border border-accent-200/60">
            <h3 className="flex items-center gap-2 text-base md:text-lg font-heading font-semibold text-accent-800 mb-4">
              <i className="ri-thumb-up-line text-accent-500"></i>
              Pros
            </h3>
            <ul className="space-y-2.5">
              {reviewContent.pros.map((pro) => (
                <li key={pro} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                  <span>{pro}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-5 md:p-6 rounded-2xl bg-secondary-50 border border-secondary-200/60">
            <h3 className="flex items-center gap-2 text-base md:text-lg font-heading font-semibold text-secondary-800 mb-4">
              <i className="ri-error-warning-line text-secondary-500"></i>
              Cons
            </h3>
            <ul className="space-y-2.5">
              {reviewContent.cons.map((con) => (
                <li key={con} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-close-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                  <span>{con}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-background-200/60 bg-background-50 mb-12">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-background-100">
                <th className="px-4 md:px-6 py-3 md:py-4 text-sm font-semibold text-foreground-800 whitespace-nowrap">
                  Feature
                </th>
                <th className="px-4 md:px-6 py-3 md:py-4 text-sm font-semibold text-primary-600 whitespace-nowrap">
                  Sugargoo
                </th>
                <th className="px-4 md:px-6 py-3 md:py-4 text-sm font-semibold text-foreground-600 whitespace-nowrap">
                  Traditional Buying
                </th>
              </tr>
            </thead>
            <tbody>
              {reviewContent.comparisonData.map((row, idx) => (
                <tr
                  key={row.feature}
                  className={`border-t border-background-100 ${
                    idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'
                  }`}
                >
                  <td className="px-4 md:px-6 py-3 md:py-4 text-sm font-medium text-foreground-800 whitespace-nowrap">
                    {row.feature}
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4 text-sm text-primary-700 font-medium">
                    {row.sugargoo}
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4 text-sm text-foreground-500">
                    {row.traditional}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div id="faq">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
            Frequently Asked Questions
          </h3>
          <div className="space-y-3 max-w-3xl mx-auto">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-background-200/60 bg-background-50 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 md:px-6 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-200"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                    {item.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-lg text-foreground-500 flex-shrink-0 transition-transform duration-300 ${
                      openFaq === idx ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-5 md:px-6 pb-4 pt-1">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}